import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Play, Square, MapPin, ChevronDown } from "lucide-react";
import type { CanvassingSession, CanvassingTerritory } from "@/hooks/useCanvassing";
import { cn } from "@/lib/utils";

interface CanvassingSessionPanelProps {
  activeSession: CanvassingSession | null;
  onStartSession: (territoryId?: string) => Promise<any>;
  onEndSession: () => Promise<void>;
  territories: CanvassingTerritory[];
}

export function CanvassingSessionPanel({
  activeSession,
  onStartSession,
  onEndSession,
  territories,
}: CanvassingSessionPanelProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const activeTerritories = territories.filter(t => t.status === "active");

  if (activeSession) {
    return (
      <div className="flex items-center gap-2">
        <Badge className="gap-1.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          {activeSession.doors_knocked} doors
        </Badge>
        <Button variant="destructive" size="sm" onClick={onEndSession} className="gap-1.5 h-9">
          <Square className="w-3.5 h-3.5" /> End
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-stretch rounded-full overflow-hidden shadow-lg ring-2 ring-emerald-500/30">
      <Button
        onClick={() => onStartSession()}
        className={cn(
          "gap-2 h-10 px-5 rounded-none rounded-l-full",
          "bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
        )}
      >
        <Play className="w-4 h-4 fill-current" /> Start Canvassing
      </Button>
      {activeTerritories.length > 0 && (
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="default"
              className="h-10 px-2.5 rounded-none rounded-r-full bg-emerald-600 hover:bg-emerald-700 border-l border-emerald-700/40 text-white"
              title="Pick territory"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-1">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Pick a territory
            </div>
            <button
              className="w-full text-left px-2 py-2 rounded hover:bg-accent text-sm flex items-center gap-2"
              onClick={() => { setPickerOpen(false); onStartSession(); }}
            >
              <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
              Freestyle (use my location)
            </button>
            <div className="h-px bg-border my-1" />
            {activeTerritories.map(t => (
              <button
                key={t.id}
                className="w-full text-left px-2 py-2 rounded hover:bg-accent text-sm"
                onClick={() => { setPickerOpen(false); onStartSession(t.id); }}
              >
                {t.name}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
